import React, {ReactElement, useEffect, useState} from "react";
import {Button, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "../../../shared/StyledComponents";
import {DW_SM, PD_LG, PD_MD, PD_SM, PD_XLG} from "../../../shared/dimens";
import {LetterspinDifficultyLevel, LetterspinGameBoard, LetterspinGameConfig, LetterspinGameData} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";

import {GAME_LETTERSPIN} from "../../Games";
import {Game} from "../../types";
import {colorGreen, colorYellow, lightGray, mediumGray, nightGray} from "../../../shared/colors";
import {StringUtil} from "../../../shared/string_util";
import {$KTS} from "../../../shared/keytext";
import {GameOverResult, GameOverResultType} from "../types";
import {Throttler} from "../../../shared/file_util";

export type LetterspinGameFragmentProps = GameFragmentProps & {}

enum LetterspinGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_INCOMPLETE, // Finished, but NOT solved
  STATUS_COMPLETE, // Finished, and solved
}

type LetterspinGameFragmentState = GameFragmentState<LetterspinGameConfig, LetterspinGameData> & {
  currentIndex: number;
  gameStatus: LetterspinGameStatus,
  message?: string,
}

function WordLetter(props: {
  index: number,
  isCurrent: boolean,
  letter: string | null | undefined,
  color: string
}): ReactElement {
  const [isCurrent, setIsCurrent] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(1);
  useEffect(() => {
    if (isCurrent && !props.isCurrent) {
      setScale(0);
      setTimeout(() => {
        setScale(1);
      }, ((props.index + 1) * 200));
    }
    setIsCurrent(props.isCurrent);
  }, [props.isCurrent]);
  return <StyledBoxColumn
    style={{
      transition: "transform 300ms ease-in-out",
      opacity: isCurrent && !props.isCurrent ? 0 : scale,
      transform: `scale(${scale}, ${scale})`,
      padding: PD_LG,
      width: 48,
      height: 48,
      border: "1px solid black",
      background: props.color,
      alignItems: "center",
      justifyContent: "center"
    }}>
    {props.letter ? <Typography variant="h5" style={{textTransform: "uppercase"}}>{props.letter}</Typography> : null}
  </StyledBoxColumn>;
}

function WordRow(props: { enteredWord: string[], isCurrent: boolean, word: string[] }): ReactElement {
  const colors: string[] = [null, null, null, null, null];
  const counts = new Map<string, number>();
  const positions = new Map<string, number[]>();
  props.word.forEach((letter, index) => {
    counts.set(letter, (counts.get(letter) || 0) + 1);
    let array = positions.get(letter);
    if (!array) {
      array = [];
      positions.set(letter, array);
    }
    array.push(index);
  });
  for (let i = 0; i < 5; i++) {
    const letter = props.enteredWord?.[i];
    if (letter) {
      if (!props.isCurrent && counts.get(letter) > 0 && positions.get(letter)?.includes(i)) {
        colors[i] = colorGreen;
        counts.set(letter, counts.get(letter) - 1);
      }
    }
  }
  for (let i = 0; i < 5; i++) {
    const letter = props.enteredWord?.[i];
    if (letter) {
      if (props.isCurrent) {
        colors[i] = lightGray;
      } else if (counts.get(letter) > 0) {
        if (!positions.get(letter)?.includes(i)) {
          colors[i] = colorYellow;
          counts.set(letter, counts.get(letter) - 1);
        }
      } else if (!colors[i]) {
        colors[i] = mediumGray;
      }
    }
  }
  return <StyledBoxRow>
    {new Array(5).fill(null).map((_, index: number) =>
      <WordLetter
        index={index}
        isCurrent={props.isCurrent}
        letter={props.enteredWord?.[index]}
        color={colors[index]}/>)}
  </StyledBoxRow>;
}


const firstRow = ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p']
const secondRow = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l']
const thirdRow = ['enter', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'del']

function KeyButton(props: { letter: string, onClick: (event) => void }): ReactElement {
  return <Button value={props.letter} onClick={props.onClick}
                 style={{minWidth: 0, paddingTop: 12, paddingBottom: 12, background: mediumGray}}>
    <Typography
      style={{pointerEvents: "none"}}
      variant={"h6"}>{props.letter}</Typography></Button>;
}

function Keyboard(props: { disabled?: boolean, onInput: (letter: string) => void }): ReactElement {
  const onClick = (event) => {
    if (props.disabled) {
      return;
    }
    props.onInput(event.target.value);
  };
  return <StyledBoxColumn style={{alignItems: "center", marginTop: PD_XLG}}>
    <StyledBoxRow>{firstRow.map(letter => <KeyButton onClick={onClick} letter={letter}/>)}</StyledBoxRow>
    <StyledBoxRow>{secondRow.map(letter => <KeyButton onClick={onClick} letter={letter}/>)}</StyledBoxRow>
    <StyledBoxRow>{thirdRow.map(letter => <KeyButton onClick={onClick} letter={letter}/>)}</StyledBoxRow>
  </StyledBoxColumn>
}

export class LetterspinGameFragment extends GameFragment<LetterspinGameFragmentProps, LetterspinGameConfig, LetterspinGameData, LetterspinGameFragmentState> {

  private board: LetterspinGameBoard;

  private readonly keyListener = (ev: KeyboardEvent) => {
    if (!this.state.gameData) {
      return;
    }
    // If disabled
    if (Boolean(this.state.message)) {
      return;
    }
    if (ev.key.length === 1 && /[a-z]/i.test(ev.key)) {
      this.onInput(ev.key);
      return;
    }
    switch (ev.code) {
      default:
        return;
      case "Backspace":
        this.onInput("del");
        break;
      case "Enter":
        this.onInput("enter");
        break;
    }
  };

  protected getGame(): Game {
    return GAME_LETTERSPIN;
  }

  protected onCreateState(): LetterspinGameFragmentState {
    return {
      ...super.onCreateState(),
      gameStatus: LetterspinGameStatus.STATUS_IN_PROGRESS,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("keydown", this.keyListener);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.keyListener);
    super.componentWillUnmount();
  }

  componentDidUpdate(prevProps: Readonly<LetterspinGameFragmentProps>, prevState: Readonly<LetterspinGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as LetterspinGameBoard;
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      currentIndex: 0,
      gameData: await LetterspinGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(GAME_LETTERSPIN, <>
      {LetterspinDifficultyLevel.VALUES.map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new LetterspinGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    return <Typography
      style={{marginLeft: PD_MD,}}>{(this.state.gameData.gamePlay.jsonData as LetterspinGameBoard).level.text}</Typography>
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play Letterspin</Typography>
          <Typography>Guess the mystery word in fewest number of tries. Each guess must be a valid five-letter word. Hit
            the enter button to submit. After each guess, the color of the tiles will change to show how close your
            guess was to the word.</Typography>
          <StyledBoxColumn style={{gap: 0, alignItems: "center"}}>
            <WordRow enteredWord={"weary".split("")} isCurrent={false} word={"wxxxx".split("")}/>
            <Typography>The letter W is in the word and in the correct spot.</Typography>
          </StyledBoxColumn>
          <StyledBoxColumn style={{gap: 0, alignItems: "center"}}>
            <WordRow enteredWord={"pills".split("")} isCurrent={false} word={"ixxxx".split("")}/>
            <Typography>The letter I is in the word but in the wrong spot.</Typography>
          </StyledBoxColumn>
          <StyledBoxColumn style={{gap: 0, alignItems: "center"}}>
            <WordRow enteredWord={"vague".split("")} isCurrent={false} word={"xxxxx".split("")}/>
            <Typography>None of the letters are in the mystery word.</Typography>
          </StyledBoxColumn>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [])}>
        <MoreHorizOutlined/>
      </Button>
    </>
  }

  protected renderGameContent(): ReactElement | null {
    if (!this.board) {
      return null;
    }
    return <StyledBoxColumn style={{flexGrow: 1, width: "100%", maxWidth: 480, margin: "auto", alignItems: "center"}}>
      <StyledBoxRow style={{height: 48, alignItems: "center", justifyContent: "center"}}>
        {this.state.message
          ? <Typography style={{
            paddingLeft: PD_LG,
            paddingRight: PD_LG,
            paddingTop: PD_SM,
            paddingBottom: PD_SM,
            background: nightGray,
            color: "white"
          }}>{this.state.message}</Typography>
          : null}
      </StyledBoxRow>
      {new Array(this.board.layout.level.maxTries).fill(null)
        .map((_, index) => <WordRow
          isCurrent={index === this.state.currentIndex}
          enteredWord={index < this.state.currentIndex ? this.board.enteredWords[index] : (index === this.state.currentIndex ? this.board.currentWord : null)}
          word={this.board.layout.word.split("")}/>)}
      {/*<Typography>{this.board.layout.word}</Typography>*/}
      <Keyboard
        disabled={Boolean(this.state.message)}
        onInput={letter => this.onInput(letter)}/>
    </StyledBoxColumn>;
  }

  private readonly throttler = new Throttler(50);

  private onInput(letter: string): void {
    this.throttler.run(() => this.onInputInternal(letter));
  }

  private onInputInternal(letter: string): void {
    if (letter === "del") {
      if (this.board.currentWord.length > 0) {
        this.board.currentWord.splice(this.board.currentWord.length - 1, 1);
      }
    } else if (letter === "enter") {
      const currentWord = this.board.currentWord.join("");
      if (!this.board.layout.allWords.includes(currentWord)) {
        this.setState({
          message: "Not in word list",
        });
        setTimeout(() => {
          this.board.currentWord.splice(0, this.board.currentWord.length);
          this.setState({message: null});
        }, 1000);
      } else {
        const moves = this.state.currentIndex + 1;
        if (currentWord === this.board.layout.word) {
          this.props.gameFragmentListener?.onStatusChanged("Game complete.");
          let resultText: string = "You completed %{level} game in %{moves} moves.";
          switch (this.state.gameConfig.level) {
            case LetterspinDifficultyLevel.EASY:
              resultText = StringUtil.format(resultText, $KTS("level", "an easy"));
              break;
            case LetterspinDifficultyLevel.MEDIUM:
              resultText = StringUtil.format(resultText, $KTS("level", "a medium"));
              break;
            case LetterspinDifficultyLevel.HARD:
              resultText = StringUtil.format(resultText, $KTS("level", "a hard"));
              break;
          }
          resultText = StringUtil.format(resultText, $KTS("moves", moves.toString()));
          this.finishGame(new GameOverResult(GAME_LETTERSPIN.id, GameOverResultType.WON, this.board.level.name, this.board.getPoints(moves), 0, resultText, false));
          this.setState({
            message: ["Magnificent", "Amazing", "Awesome", "Great", "Good Job", "Phew", "Ooof!"][this.state.currentIndex],
            gameStatus: LetterspinGameStatus.STATUS_COMPLETE,
          });
        } else if (this.state.currentIndex === this.board.level.maxTries - 1) {
          this.props.gameFragmentListener?.onStatusChanged("Game complete.");
          this.finishGame(new GameOverResult(GAME_LETTERSPIN.id, GameOverResultType.LOST, this.board.level.name, 0, 0, "You lost this game.", false));
          this.setState({
            message: this.board.layout.word.toUpperCase(),
            gameStatus: LetterspinGameStatus.STATUS_COMPLETE,
          });
        }
        this.board.enteredWords.push([...this.board.currentWord]);
        this.board.currentWord.splice(0, this.board.currentWord.length);
        this.setState({
          currentIndex: this.state.currentIndex + 1,
        });
      }
    } else {
      if (this.board.currentWord.length < 5) {
        this.board.currentWord.push(letter);
      }
    }
    this.forceUpdate();
  }
}